import type { Template, UserPreferences } from '../api/types';
import type { ContractTemplate } from '../contract/types';
import { STORAGE_KEYS } from '../utils/constants';

type TemplateStore = Record<string, Template[]>;

function safeParse<T>(raw: string | null, fallback: T): T {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

import { schedulePush } from './cloudSync';

/**
 * 写失败（几乎总是 localStorage 配额爆了 —— 印章/水印图是 dataURL，两张 2MB
 * 的 PNG 就能撑爆 5MB 配额）必须让用户知道。以前静默吞掉：界面照常显示，
 * 一刷新这次之后的所有编辑全没了，客户只会说「模板又丢了」。
 */
function notifyStorageFailure(): void {
  try {
    window.dispatchEvent(new CustomEvent('pp-storage-failed'));
  } catch {
    /* 非浏览器环境忽略 */
  }
}

function safeWrite(key: string, value: unknown): boolean {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    // 模板 store 变更 → 防抖推送云端（preferences 不上云）
    if (key === STORAGE_KEYS.templates || key === STORAGE_KEYS.contractTemplates) {
      schedulePush();
    }
    return true;
  } catch {
    notifyStorageFailure();
    return false;
  }
}

// ---- 删除墓碑 ----

export type Tombstones = Record<string, number>;

export function loadTombstones(): Tombstones {
  return safeParse<Tombstones>(localStorage.getItem(STORAGE_KEYS.deletedTemplates), {});
}

/** 记下被删掉的模板 id，云同步据此不再把它们合并回来。 */
function recordDeletions(prev: Array<{ id: string }>, next: Array<{ id: string }>): void {
  const keep = new Set(next.map((t) => t.id));
  const gone = prev.filter((t) => t?.id && !keep.has(t.id));
  if (gone.length === 0) return;
  const store = loadTombstones();
  const now = Date.now();
  for (const t of gone) store[t.id] = now;
  try {
    localStorage.setItem(STORAGE_KEYS.deletedTemplates, JSON.stringify(store));
  } catch {
    /* 墓碑写不进去不影响主流程 */
  }
}

export function loadAllTemplates(): TemplateStore {
  return safeParse<TemplateStore>(localStorage.getItem(STORAGE_KEYS.templates), {});
}

export function loadTemplatesForTable(tableId: string): Template[] {
  return loadAllTemplates()[tableId] ?? [];
}

export function saveTemplatesForTable(tableId: string, templates: Template[]): boolean {
  const all = loadAllTemplates();
  // 不再在保存时截断 —— 超额由创建入口拦，静默丢掉用户已有的模板是最糟的处理
  recordDeletions(all[tableId] ?? [], templates);
  all[tableId] = templates.slice();
  return safeWrite(STORAGE_KEYS.templates, all);
}

export function loadPreferences(): UserPreferences {
  return safeParse<UserPreferences>(localStorage.getItem(STORAGE_KEYS.preferences), {
    lastTemplateIdByTable: {},
  });
}

export function savePreferences(prefs: UserPreferences): boolean {
  return safeWrite(STORAGE_KEYS.preferences, prefs);
}

// ---- Contract templates ----

type ContractTemplateStore = Record<string, ContractTemplate[]>;

export function loadContractTemplatesForTable(tableId: string): ContractTemplate[] {
  const all = safeParse<ContractTemplateStore>(
    localStorage.getItem(STORAGE_KEYS.contractTemplates),
    {},
  );
  return all[tableId] ?? [];
}

export function saveContractTemplatesForTable(
  tableId: string,
  templates: ContractTemplate[],
): boolean {
  const all = safeParse<ContractTemplateStore>(
    localStorage.getItem(STORAGE_KEYS.contractTemplates),
    {},
  );
  recordDeletions(all[tableId] ?? [], templates);
  all[tableId] = templates.slice();
  return safeWrite(STORAGE_KEYS.contractTemplates, all);
}
